
import React from "react";
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { translateExerciseName } from "@/utils/exerciseTranslations";
import { getTestColor } from "@/components/putting/training/charts/TestColorConfig";

interface ExerciseProgressionChartProps {
  testGroups: Record<string, any[]>;
  testIdToName?: Record<string, string>; // Map testId to display name for translation
}

const ExerciseProgressionChart: React.FC<ExerciseProgressionChartProps> = ({
  testGroups,
  testIdToName = {}
}) => {
  const { t } = useTranslation('common');
  
  // Fallback colors array if getTestColor doesn't find a match
  const colors = [
    "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", 
    "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#6366f1"
  ];

  return (
    <div className="h-[400px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            tickMargin={8}
          />
          <YAxis 
            domain={[0, 15]}
            tick={{ fontSize: 12 }}
            tickMargin={8}
            label={{ value: t('puttingTraining.charts.axes.points'), angle: -90, position: 'insideLeft' }}
          />
          <Tooltip 
            formatter={(value, name) => [`${value} pts`, name]}
          />
          <Legend />
          
          {Object.keys(testGroups).map((testId, testIndex) => {
            // Use testId for color (consistent across languages)
            const color = getTestColor(testId) || colors[testIndex % colors.length];
            // Use display name for legend (translated)
            const displayName = testIdToName[testId] || testId;
            console.log(`ExerciseProgressionChart - TestId: ${testId}, DisplayName: ${displayName}, Color: ${color}`);
            return (
              <Line
                key={testId}
                data={testGroups[testId]}
                type="monotone"
                dataKey="points"
                name={translateExerciseName(displayName, t)}
                stroke={color}
                strokeWidth={3}
                dot={{ strokeWidth: 2, r: 5, fill: color }}
                activeDot={{ strokeWidth: 0, r: 7, fill: color }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ExerciseProgressionChart;
